import { useCallback, useState } from 'react'

import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'
import useSentry from 'Pods/Shared/Hooks/useSentry'

export const RETRY_STATE = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUBMITTED: 'submitted',
}

function useSubscription({
  subscriptionData,
  messages,
  onRestoreSuccess,
  onRestoreError,
  onRedirectToPaymentForm,
  onCancelPlanSwitchSuccess,
}) {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [retryState, setRetryState] = useState(RETRY_STATE.IDLE)
  const { postData, isLoading } = useApiPost()
  const { deleteData, isLoading: isCancelPlanSwitchLoading } = useApiDelete()
  const { showSuccess, showError } = useNotifications()
  const { captureException } = useSentry()

  const handleOpenCancelModal = useCallback(() => {
    setIsCancelModalOpen(true)
  }, [])

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false)
  }, [])

  const handleCancelSubscription = useCallback(
    (reason) => {
      if (!subscriptionData?.id) return

      postData({
        path: '/api/subscriptions/cancel_subscription',
        data: { id: subscriptionData.id, reason },
        onSuccess: () => {
          window.location.reload()
        },
        onError: (error) => {
          const errorMessage =
            error.response?.data?.error_message || messages.cancelError
          showError(errorMessage)
          captureException(error, {
            tags: { context: 'cancel_subscription' },
            extra: { subscriptionId: subscriptionData.id, reason },
          })
        },
      })
    },
    [subscriptionData, messages, postData, showError, captureException]
  )

  const handleRestoreSubscription = useCallback(() => {
    if (!subscriptionData?.id) return

    postData({
      path: '/api/subscriptions/restore_subscription',
      data: { id: subscriptionData.id },
      onSuccess: (response) => {
        if (response?.data?.redirectTo) {
          onRedirectToPaymentForm?.(response.data.redirectTo)
          return
        }

        const renewalScheduled = Boolean(response?.data?.renewal_scheduled)

        showSuccess(messages.restoreSuccess)
        onRestoreSuccess?.({ renewalScheduled })
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.error_message || messages.restoreError
        showError(errorMessage)
        captureException(error, {
          tags: { context: 'restore_subscription' },
          extra: { subscriptionId: subscriptionData.id },
        })
        onRestoreError?.(errorMessage)
      },
    })
  }, [
    subscriptionData,
    messages,
    onRestoreSuccess,
    onRestoreError,
    onRedirectToPaymentForm,
    postData,
    showSuccess,
    showError,
    captureException,
  ])

  const handleRetryPayment = useCallback(() => {
    if (!subscriptionData?.id) return

    setRetryState(RETRY_STATE.LOADING)

    postData({
      path: '/profile/subscription/retry-payment',
      onSuccess: () => {
        setRetryState(RETRY_STATE.SUBMITTED)
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.error_message || messages.retryError
        showError(errorMessage)
        setRetryState(RETRY_STATE.IDLE)
        captureException(error, {
          tags: { context: 'retry_payment' },
          extra: { subscriptionId: subscriptionData.id },
        })
      },
    })
  }, [subscriptionData, messages, postData, showError, captureException])

  const handleCancelPlanSwitch = useCallback(() => {
    deleteData({
      path: '/profile/subscription/plan_switch',
      onSuccess: () => {
        showSuccess(messages.cancelPlanSwitchSuccess)
        onCancelPlanSwitchSuccess?.()
      },
      onError: (error) => {
        showError(error.response?.data?.error || messages.cancelPlanSwitchError)
        captureException(error, { tags: { context: 'cancel_plan_switch' } })
      },
    })
  }, [deleteData, messages, showSuccess, showError, captureException, onCancelPlanSwitchSuccess])

  return {
    isCancelModalOpen,
    isLoading,
    handleOpenCancelModal,
    handleCloseCancelModal,
    handleCancelSubscription,
    // Both call restore_subscription — backend determines action based on subscription state
    handleRenewSubscription: handleRestoreSubscription,
    handleReactivateSubscription: handleRestoreSubscription,
    handleRetryPayment,
    retryState,
    handleCancelPlanSwitch,
    isCancelPlanSwitchLoading,
  }
}

export default useSubscription
